import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Camera, RotateCcw, Loader2, ImagePlus } from "lucide-react";
import { toast } from "sonner";

const TEAL = "#0D9488";

// Local YYYY-MM-DD so the re-logged meal lands on the user's own "today".
function localDayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * "My meals" — a visual wall of every AI meal photo the user has logged.
 * One tap re-logs the meal into today (macros + note + thumbnail preserved).
 *
 * Hidden while the user has no photo entries so it doesn't take up space on empty accounts.
 */
export default function PhotoMealGallery({ refreshKey = 0, onRelog }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloggingId, setReloggingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/nutrition/meal-photos", { params: { limit: 24 } });
      setEntries(data?.entries || []);
    } catch { /* silent — gallery is a nicety */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const relog = async (e) => {
    setReloggingId(e.id);
    try {
      await api.post("/nutrition", {
        calories: e.calories,
        protein_g: e.protein_g,
        carbs_g: e.carbs_g,
        fat_g: e.fat_g,
        sugar_g: e.sugar_g,
        fiber_g: e.fiber_g,
        sodium_mg: e.sodium_mg,
        meal_group: e.meal_group,
        source: "photo",
        note: e.note,
        thumbnail_b64: e.thumbnail_b64 || null,
        day: localDayISO(),
      });
      onRelog?.();
      toast.success(`Re-logged: ${e.note || `${Math.round(e.calories)} kcal`}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setReloggingId(null); }
  };

  if (loading) return null;
  if (!entries.length) return null;

  return (
    <Card
      className="border border-slate-200 bg-white rounded-xl p-5"
      data-testid="photo-meal-gallery"
    >
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-teal-50"
            style={{ color: TEAL }}
          >
            <ImagePlus className="w-4 h-4" strokeWidth={2.2} />
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: TEAL }}>
              My Meals
            </div>
            <div className="text-sm text-slate-700 mt-0.5">
              {entries.length} favourite photo{entries.length === 1 ? "" : "s"} · tap to re-log
            </div>
          </div>
        </div>
        <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-400">
          <Camera className="w-3 h-3 inline mr-1" />Snap · Parse · Reuse
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
        {entries.map((e) => {
          const label = (e.note && e.note.trim()) || `${Math.round(e.calories)} kcal meal`;
          const busy = reloggingId === e.id;
          return (
            <button
              key={e.id}
              type="button"
              onClick={() => relog(e)}
              disabled={busy}
              className="group relative aspect-square rounded-lg overflow-hidden border border-slate-200 hover:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 transition-colors disabled:opacity-70"
              title={`Re-log: ${label} · ${Math.round(e.calories)} kcal · ${Math.round(e.protein_g)}g P`}
              data-testid={`meal-photo-tile-${e.id}`}
            >
              <img
                src={e.thumbnail_b64}
                alt={label}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              {/* Bottom gradient with label */}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-2 text-left">
                <div className="text-[11px] font-semibold text-white leading-tight line-clamp-2">
                  {label}
                </div>
                <div className="font-mono-num text-[9px] uppercase tracking-widest text-white/80 mt-0.5">
                  {Math.round(e.calories)}kcal · {Math.round(e.protein_g)}g P
                </div>
              </div>
              {/* Re-log affordance on hover */}
              <div className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/95 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm">
                {busy
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: TEAL }} />
                  : <RotateCcw className="w-3.5 h-3.5" style={{ color: TEAL }} />
                }
              </div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}
